/**
 * Calculate working days (Monday-Friday) between two dates
 * @param startDate Start date (YYYY-MM-DD or Date)
 * @param endDate End date (YYYY-MM-DD or Date)
 * @returns Number of working days (excluding weekends)
 */
export function calculateWorkingDays(startDate: string | Date, endDate: string | Date): number {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  if (start > end) {
    return 0;
  }

  let workingDays = 0;
  const current = new Date(start);

  while (current <= end) {
    const dayOfWeek = current.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workingDays++;
    }
    current.setDate(current.getDate() + 1);
  }

  return workingDays;
}

/**
 * Format date to YYYY-MM-DD
 * @param date Date object, date-picker value, or ISO/local date string
 * @returns Formatted date string
 */
export function formatDateToYYYYMMDD(date: Date | string | { toDate?: () => Date }): string {
  if (typeof date === 'string') {
    const calendarDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    if (calendarDate) return `${calendarDate[1]}-${calendarDate[2]}-${calendarDate[3]}`;
  }
  const pickerDate = date && typeof date === 'object' && !(date instanceof Date) && typeof date.toDate === 'function'
    ? date.toDate()
    : date;
  const value = pickerDate instanceof Date ? pickerDate : new Date(pickerDate as string);
  if (Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if date is in the past
 * @param date Date to check
 * @returns True if date is in the past
 */
export function isPastDate(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Get minimum date (today)
 * @returns Today's date
 */
export function getMinDate(): Date {
  return new Date();
}
